import { Component, OnInit, inject } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import GridsConfig from "@app/common/grid.config";
import {
  Migration,
  MigrationStatisticsRow,
  MigrationExecutionStep,
  MigrationExecutionStoredProcedure,
} from "@app/interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { ApiService } from "@app/services/api.service";
import { AuthService } from "@app/auth/auth.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStore } from "@app/services/migration.store";
import { ColumnSortConfig } from "@app/components/table-grid/table-grid/table-grid.component";
import { catchError, map, of, tap } from "rxjs";
import { Chart, ChartConfiguration, registerables } from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

Chart.register(...registerables);

@Component({
  selector: "app-statistics",
  templateUrl: "./statistics.component.html",
  styleUrls: ["./statistics.component.scss"],
  standalone: false,
})
export class StatisticsComponent implements OnInit {
  private pendingQueryMigrationId: number | null = null;
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly migrationStore = inject(MigrationStore);
  private readonly migrationService = inject(MigrationService);
  readonly authService = inject(AuthService);

  hasAdminGroup$ = this.authService.hasAdminGroup$;

  migrations: Migration[] = [];
  selectedMigrationId: number | null = null;
  migrationId: number | null = null;
  migration: Migration | null = null;
  activeTabIndex = 0;
  loading = false;
  loadingMigrations = false;
  error = false;
  statisticsLoadCompleted = false;

  statisticsColumns = GridsConfig.MigrationStatistics.Columns;
  statisticsTableHeaders = GridsConfig.MigrationStatistics.Headers;
  statisticsColumnWidths = GridsConfig.MigrationStatistics.Widths;
  statisticsRows: MigrationStatisticsRow[] = [];
  statisticsRowsPaginated: MigrationStatisticsRow[] = [];
  nameFilterOptions: string[] = [];
  selectedNameFilter: string | null = null;
  protocolIdFilterOptions: number[] = [];
  selectedProtocolIdFilter: number | null = null;
  migrationsViewMode: "table" | "diagram" = "table";
  deletedMigrationsViewMode: "table" | "diagram" = "table";
  statisticsPaginatorFilter: PaginatorFilter = new PaginatorFilter({
    pageSize: 10,
  });

  // Diagram/Timeline chart data
  diagramChartData: Array<{ name: string; value: number }> = [];
  barChartType: "bar" = "bar";
  barChartData: ChartConfiguration<"bar">["data"] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: "Duration (s)",
        backgroundColor: "#3b82f6",
        borderRadius: 6,
        borderWidth: 0,
      },
    ],
  };
  barChartOptions: ChartConfiguration<"bar">["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Duration (s)",
        },
      },
      y: {
        title: {
          display: true,
          text: "Migration Stages",
        },
      },
    },
  };

  /** Column type configuration for type-aware sorting */
  readonly columnSortTypes: ColumnSortConfig = {
    name: "string",
    protocol_id: "number",
    status: "string",
    started: "date",
    ended: "date",
    durationText: "string",
    description: "string",
  };

  /** Current sort state */
  currentSort: Sort = { active: "started", direction: "asc" };

  ngOnInit(): void {
    this.selectedMigrationId = null;

    this.route.queryParamMap.subscribe((params) => {
      const queryMigrationId = params.get("migrationId");
      if (queryMigrationId && !Number.isNaN(Number(queryMigrationId))) {
        this.pendingQueryMigrationId = Number(queryMigrationId);
      } else {
        this.pendingQueryMigrationId = null;
      }

      this.loadMigrations();
    });
  }

  paginateStatistics(event: PageEvent): void {
    this.statisticsPaginatorFilter.currentPage = event.pageIndex;
    this.statisticsPaginatorFilter.pageSize = event.pageSize;
    this.applyStatisticsPagination();
  }

  /** Handle sort changes from table-grid */
  onSortChanged(sort: Sort): void {
    this.currentSort = sort;
    this.statisticsPaginatorFilter.currentPage = 0; // Reset to first page on sort change
    this.applyStatisticsPagination();
  }

  onNameFilterChanged(): void {
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
  }

  onProtocolIdFilterChanged(): void {
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onMigrationsViewToggle(checked: boolean): void {
    this.migrationsViewMode = checked ? "diagram" : "table";
    if (checked) {
      this.selectedNameFilter = null;
    }
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onDeletedMigrationsViewToggle(checked: boolean): void {
    this.deletedMigrationsViewMode = checked ? "diagram" : "table";
    if (checked) {
      this.selectedNameFilter = null;
    }
    this.statisticsPaginatorFilter.currentPage = 0;
    this.applyStatisticsPagination();
    this.updateDiagramChart();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.activeTabIndex = event.index;
    this.resetSelectedMigrationState();

    if (this.activeTabIndex === 1) {
      this.loadDeletedMigrations();
      return;
    }

    this.loadMigrations();
  }

  onMigrationSelectionChanged(): void {
    if (!this.selectedMigrationId) {
      this.migrationId = null;
      this.migration = null;
      this.statisticsRows = [];
      this.statisticsRowsPaginated = [];
      this.nameFilterOptions = [];
      this.selectedNameFilter = null;
      this.protocolIdFilterOptions = [];
      this.selectedProtocolIdFilter = null;
      this.statisticsPaginatorFilter.count = 0;
      this.statisticsLoadCompleted = false;
      return;
    }

    this.migrationId = this.selectedMigrationId;
    this.migrationStore.setCurrentMigrationId(String(this.migrationId));
    this.loadMigrationStatistics(this.migrationId);
  }

  exportStatistics(): void {
    if (this.statisticsRows.length === 0 || !this.migrationId) {
      return;
    }

    this.exportStatisticsPdf();
  }

  private async exportStatisticsPdf(): Promise<void> {
    const filename = `migration-statistics-${this.migrationId}-${this.migration?.company_name}-${new Date().getTime()}`;
    const migrationName = this.migration?.company_name || "N/A";
    const exportRows = this.getSortedStatistics();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(16);
    doc.text(`Migration Statistics - ${migrationName}`, 40, 40);
    doc.setFontSize(10);
    doc.text(`Migration ID: ${this.migrationId}`, 40, 58);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 72);

    autoTable(doc, {
      startY: 88,
      head: [this.statisticsTableHeaders],
      body: exportRows.map((row) =>
        this.statisticsColumns.map((column) =>
          this.getExportCellValue(
            row as unknown as Record<string, unknown>,
            column,
          ),
        ),
      ),
      styles: {
        fontSize: 8,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [8, 53, 102],
      },
      margin: {
        left: 30,
        right: 30,
      },
      theme: "striped",
    });

    doc.addPage("a4", "landscape");
    doc.setFontSize(16);
    doc.text("Migration Timeline Chart", 40, 40);

    this.updateDiagramChart();
    const chartImage = await this.createChartImageForPdf();

    if (chartImage) {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const imageWidth = pageWidth - margin * 2;
      const imageHeight = pageHeight - 100;

      doc.addImage(chartImage, "PNG", margin, 60, imageWidth, imageHeight);
    } else {
      doc.setFontSize(11);
      doc.text("No chart data is available for this migration.", 40, 72);
    }

    doc.save(`${filename}.pdf`);
  }

  private async createChartImageForPdf(): Promise<string | null> {
    if (!this.diagramChartData || this.diagramChartData.length === 0) {
      return null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 900;

    const context = canvas.getContext("2d");
    if (!context) {
      return null;
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    const chartOptions: ChartConfiguration<"bar">["options"] = {
      ...this.barChartOptions,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
    };

    const chart = new Chart(context, {
      type: this.barChartType,
      data: this.barChartData,
      options: chartOptions,
    });

    chart.update();
    await new Promise((resolve) => setTimeout(resolve, 50));

    const image = canvas.toDataURL("image/png");
    chart.destroy();
    return image;
  }

  private getExportCellValue(
    row: Record<string, unknown>,
    column: string,
  ): string {
    const value = row[column];

    if (value === null || value === undefined) {
      return "";
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    return JSON.stringify(value);
  }

  private loadMigrations(): void {
    this.changeLoadingState(true);

    console.log("[Statistics] Loading migrations...");

    this.apiService
      .getMigrations()
      .pipe(
        tap((response: Migration[]) => {
          console.log(
            "[Statistics] Got migrations response:",
            response.length,
            "items",
          );
        }),
        map((migrations: Migration[]) =>
          migrations.map((migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
        ),
        tap((processedMigrations: Migration[]) => {
          console.log(
            "[Statistics] Processed migrations:",
            processedMigrations.length,
            "items",
          );
          this.migrations = processedMigrations.sort(
            (a, b) => (b.id || 0) - (a.id || 0),
          );
          this.tryApplyPendingSelection();
          this.changeLoadingState(false);
        }),
        catchError((error) => {
          console.error("[Statistics] Error loading migrations:", error);
          this.migrations = [];
          this.changeLoadingState(false);
          return of([]);
        }),
      )
      .subscribe((result) => {
        console.log(
          "[Statistics] Subscribe completed with",
          result?.length || 0,
          "migrations",
        );
      });
  }

  private loadDeletedMigrations(): void {
    this.changeLoadingState(true);

    console.log("[Statistics] Loading deleted migrations...");

    this.apiService
      .getDeletedMigrations()
      .pipe(
        tap((response: Migration[]) => {
          console.log(
            "[Statistics] Got deleted migrations response:",
            response.length,
            "items",
          );
        }),
        map((migrations: Migration[]) =>
          migrations.map((migration) =>
            this.migrationService.processMigrationRecord(migration),
          ),
        ),
        tap((processedMigrations: Migration[]) => {
          console.log(
            "[Statistics] Processed deleted migrations:",
            processedMigrations.length,
            "items",
          );
          this.migrations = processedMigrations.sort(
            (a, b) => (b.id || 0) - (a.id || 0),
          );
          this.changeLoadingState(false);
          this.tryApplyPendingSelection();
        }),
        catchError((error) => {
          console.error(
            "[Statistics] Error loading deleted migrations:",
            error,
          );
          this.migrations = [];
          this.changeLoadingState(false);
          return of([]);
        }),
      )
      .subscribe((result) => {
        console.log(
          "[Statistics] Deleted migrations subscribe completed with",
          result?.length || 0,
          "migrations",
        );
      });
  }

  private loadMigrationStatistics(migrationId: number): void {
    this.loading = true;
    this.error = false;
    this.statisticsLoadCompleted = false;
    this.migrationStore.setLoading(true);

    const migrationDetailsRequest =
      this.activeTabIndex === 1
        ? this.apiService.getDeletedMigrationDetails(migrationId)
        : this.apiService.getMigrationDetails(migrationId);

    migrationDetailsRequest
      .pipe(
        map((migration: Migration) =>
          this.migrationService.processMigrationRecord(migration),
        ),
        tap((processedMigration: Migration) => {
          this.migration = processedMigration;
          this.statisticsRows =
            this.migrationService.processMigrationStatisticsRows(
              processedMigration,
              "Z",
            );
          this.nameFilterOptions = Array.from(
            new Set(
              this.statisticsRows
                .map((row) => (row.name || "").trim())
                .filter((name) => !!name),
            ),
          ).sort((a, b) => a.localeCompare(b));
          this.selectedNameFilter = null;
          this.protocolIdFilterOptions = Array.from(
            new Set(
              this.statisticsRows
                .map((row) => row.protocol_id)
                .filter((id) => id != null && id !== "")
                .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
                .filter((id) => typeof id === "number" && !isNaN(id)),
            ),
          ).sort((a, b) => a - b);
          this.selectedProtocolIdFilter = null;
          this.statisticsPaginatorFilter.currentPage = 0;
          this.applyStatisticsPagination();
          this.updateDiagramChart();
          this.loading = false;
          this.statisticsLoadCompleted = true;
          this.migrationStore.setLoading(false);
        }),
        catchError(() => {
          this.error = true;
          this.loading = false;
          this.statisticsLoadCompleted = true;
          this.migration = null;
          this.statisticsRows = [];
          this.statisticsRowsPaginated = [];
          this.nameFilterOptions = [];
          this.selectedNameFilter = null;
          this.protocolIdFilterOptions = [];
          this.selectedProtocolIdFilter = null;
          this.statisticsPaginatorFilter.count = 0;
          this.migrationStore.setLoading(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  getMigrationDisplayLabel(migration: Migration): string {
    const createdDate = (migration.created_dt || "")
      .split("T")[0]
      .split(" ")[0];

    return `${migration.id} - ${migration.company_name} - (${createdDate})`;
  }

  getSelectedMigrationDisplayLabel(): string {
    const selectedMigration = this.migrations.find(
      (migration) => migration.id === this.selectedMigrationId,
    );

    return selectedMigration
      ? this.getMigrationDisplayLabel(selectedMigration)
      : "-- None --";
  }

  private resetSelectedMigrationState(): void {
    this.selectedMigrationId = null;
    this.migrationId = null;
    this.migration = null;
    this.error = false;
    this.loading = false;
    this.statisticsLoadCompleted = false;
    this.statisticsRows = [];
    this.statisticsRowsPaginated = [];
    this.nameFilterOptions = [];
    this.selectedNameFilter = null;
    this.protocolIdFilterOptions = [];
    this.selectedProtocolIdFilter = null;
    this.migrationsViewMode = "table";
    this.deletedMigrationsViewMode = "table";
    this.statisticsPaginatorFilter.currentPage = 0;
    this.statisticsPaginatorFilter.count = 0;
  }

  private tryApplyPendingSelection(): void {
    if (!this.pendingQueryMigrationId) {
      return;
    }

    const matchingMigration = this.migrations.find(
      (migration) => migration.id === this.pendingQueryMigrationId,
    );

    if (!matchingMigration) {
      return;
    }

    this.selectedMigrationId = this.pendingQueryMigrationId;
    this.pendingQueryMigrationId = null;
    this.onMigrationSelectionChanged();
  }

  private applyStatisticsPagination(): void {
    // Sort the full dataset, then paginate
    const sorted = this.getSortedStatistics();
    const startIndex =
      this.statisticsPaginatorFilter.currentPage *
      this.statisticsPaginatorFilter.pageSize;
    const endIndex = startIndex + this.statisticsPaginatorFilter.pageSize;
    this.statisticsRowsPaginated = sorted.slice(startIndex, endIndex);
    this.statisticsPaginatorFilter.count = sorted.length;
  }

  private getFilteredStatistics(): MigrationStatisticsRow[] {
    const isDiagramView =
      this.activeTabIndex === 0
        ? this.migrationsViewMode === "diagram"
        : this.deletedMigrationsViewMode === "diagram";

    const hasNameFilter =
      !isDiagramView &&
      !!this.selectedNameFilter &&
      this.selectedNameFilter.trim() !== "";
    const hasProtocolIdFilter = this.selectedProtocolIdFilter !== null;

    if (!hasNameFilter && !hasProtocolIdFilter) {
      return [...this.statisticsRows];
    }

    return this.statisticsRows.filter((row) => {
      const rowProtocolId =
        typeof row.protocol_id === "string"
          ? parseInt(row.protocol_id, 10)
          : row.protocol_id;

      const nameMatch =
        !hasNameFilter || (row.name || "").trim() === this.selectedNameFilter;
      const protocolIdMatch =
        !hasProtocolIdFilter ||
        (typeof rowProtocolId === "number" &&
          !isNaN(rowProtocolId) &&
          rowProtocolId === this.selectedProtocolIdFilter);

      return nameMatch && protocolIdMatch;
    });
  }

  /** Return statistics rows sorted by the current sort state */
  private getSortedStatistics(): MigrationStatisticsRow[] {
    const dataToSort = this.getFilteredStatistics();

    if (!this.currentSort.active || this.currentSort.direction === "") {
      // No active sort — default to started ascending
      return [...dataToSort].sort(
        (a, b) =>
          new Date(a.startedSortValue || "").getTime() -
          new Date(b.startedSortValue || "").getTime(),
      );
    }

    const direction = this.currentSort.direction === "asc" ? 1 : -1;
    const active = this.currentSort.active;
    const sortType = this.columnSortTypes[active] || "string";

    let sortedData = [...dataToSort].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortType) {
        case "number":
          aVal = (a as any)[active] ?? 0;
          bVal = (b as any)[active] ?? 0;
          break;
        case "date":
          aVal = (a as any)[active]
            ? new Date((a as any)[active]).getTime()
            : 0;
          bVal = (b as any)[active]
            ? new Date((b as any)[active]).getTime()
            : 0;
          break;
        case "string":
        default:
          // For durationText, sort by underlying numeric duration
          if (active === "durationText") {
            aVal = a.duration ?? 0;
            bVal = b.duration ?? 0;
          } else {
            aVal = ((a as any)[active] || "").toString().toLowerCase();
            bVal = ((b as any)[active] || "").toString().toLowerCase();
          }

          break;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
    return sortedData;
  }

  private changeLoadingState(state: boolean) {
    this.loading = state;
    this.loadingMigrations = state;
    this.migrationStore.setLoading(state);
  }

  /** Generate timeline chart data for diagram tab */
  private generateDiagramChart(): void {
    if (!this.migration || !this.migration.statistics_obj) {
      this.diagramChartData = [];
      this.barChartData = {
        labels: [],
        datasets: [{ ...this.barChartData.datasets[0], data: [] }],
      };
      return;
    }

    const steps = this.migration.statistics_obj.steps || [];
    const durationMap = new Map<string, number>();

    steps.forEach((step: MigrationExecutionStep) => {
      if (step.name === "stage_company") {
        // For stage_company, get unique procedures by name and compute their durations
        const procedureMap = new Map<
          string,
          MigrationExecutionStoredProcedure[]
        >();

        if (step.store_procedures && step.store_procedures.length > 0) {
          const filteredProcedures =
            this.selectedProtocolIdFilter === null
              ? step.store_procedures
              : step.store_procedures.filter(
                  (proc: MigrationExecutionStoredProcedure) =>
                    proc.protocol_id === this.selectedProtocolIdFilter,
                );

          if (filteredProcedures.length === 0) {
            return;
          }

          filteredProcedures.forEach(
            (proc: MigrationExecutionStoredProcedure) => {
              const name = proc.name;
              if (!procedureMap.has(name)) {
                procedureMap.set(name, []);
              }
              procedureMap.get(name)!.push(proc);
            },
          );
        }

        // Create an entry for each unique procedure name
        procedureMap.forEach((procedures, procName) => {
          // const procStart = procedures.reduce((min, p) => {
          //   const pStart = new Date(p.started || "").getTime();
          //   return pStart < min ? pStart : min;
          // }, Infinity);
          // const procEnd = procedures.reduce((max, p) => {
          //   const pEnd = new Date(p.ended || "").getTime();
          //   return pEnd > max ? pEnd : max;
          // }, -Infinity);

          let proc_total_time = 0;
          procedures.forEach((p) => {
            if (p.started && p.ended) {
              proc_total_time +=
                new Date(p.ended).getTime() - new Date(p.started).getTime();
            }
          });

          durationMap.set(procName, proc_total_time);
          // if (procStart !== Infinity && procEnd !== -Infinity) {
          //   const duration = procEnd - procStart;
          //   durationMap.set(
          //     procName,
          //     (durationMap.get(procName) || 0) + duration,
          //   );
          // }
        });
      } else {
        // Regular step: use started and ended times
        const stepStart = new Date(step.started || "").getTime();
        const stepEnd = new Date(step.ended || "").getTime();

        if (stepStart && stepEnd) {
          const duration = stepEnd - stepStart;
          durationMap.set(
            step.name,
            (durationMap.get(step.name) || 0) + duration,
          );
        }
      }
    });

    this.diagramChartData = Array.from(durationMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    this.barChartData = {
      labels: this.diagramChartData.map((item) => item.name),
      datasets: [
        {
          data: this.diagramChartData.map((item) =>
            Number((item.value / 1000).toFixed(2)),
          ),
          label: "Duration (s)",
          backgroundColor: "#3b82f6",
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    };
  }

  /** Update diagram when migration is loaded */
  private updateDiagramChart(): void {
    if (this.migration) {
      this.generateDiagramChart();
    }
  }
}
